// helpers/search/searchQuery.ts
// Contrat d'URL de la page /pet-sitter/search, utilisé par les liens internes et par les formulaires externes (WordPress) :
//   ?service=garde-chez-eux
//   &address=Paris%2011e&lat=48.85&lng=2.37
//   &dog=1&cat=0&nac=0
//   &start=2026-11-02&end=2026-11-05   (YYYY-MM-DD)
//   Promenade / visite uniquement :
//   &frequency=once|daily|every_2_days|every_3_days|custom
//   &days=mon,wed,fri                  (frequency=custom)
//   &times=09:00,18:00&durations=hour,half_hour   (un horaire + une durée par passage, 4 max)
//   &src=wp_form_full                  (présent => arrivée externe : la recherche précédente est remise à zéro)
import type { ParsedUrlQuery } from 'querystring'
import type { PetToComplete, ServiceSlug, SlotsByService } from '@/store/newRequestCartStore'
import {
  buildRecurrentDateList,
  DateTimeInstance,
  durationOptions,
  getSlotType,
  makeBlankSlot,
  MAX_RECURRENT_MONTHS,
  MAX_RECURRENT_SLOTS,
  MAX_SERVICE_DAY_COUNT,
  RECURRENT_FREQUENCIES,
  serviceConfig,
  WEEKDAY_FIELDS,
} from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import { getDefaultSearchDistance } from '@/helpers/search/searchFilters'

const PET_SLUGS = ['dog', 'cat', 'nac'] as const
const WEEKDAY_SLUGS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

type Frequency = (typeof RECURRENT_FREQUENCIES)[number]
type TimePeriod = { timeStart: string; serviceDuration: string }

export type SearchQuery = {
  service: ServiceSlug | null
  address: { formatted: string; latitude: number; longitude: number } | null
  pets: PetToComplete[] | null
  period: { start: string; end: string | null } | null
  // Promenade / visite : fréquence, jours de la semaine et horaires
  schedule: {
    frequency: Frequency | null
    customDays: DateTimeInstance['customDays'] | null
    timePeriods: TimePeriod[]
  } | null
  source: string | null
}

export type SearchStorePatch = {
  selectedService?: string | null
  address?: SearchQuery['address']
  distance?: { min: number; max: number }
  petsToComplete?: PetToComplete[]
  formSlotsByService?: SlotsByService
  hasCompleteSearch?: boolean
}

export const getSingleQueryParam = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value)

const parseNumberQueryParam = (value: string | string[] | undefined) => {
  const raw = getSingleQueryParam(value)
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

const parseListQueryParam = (value: string | string[] | undefined) => {
  const raw = getSingleQueryParam(value)
  return raw == null ? null : raw.split(',').map((item) => item.trim())
}

const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const toHM = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

const isValidYMD = (value?: string): value is string => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [y, m, d] = value.split('-').map(Number)
  return toYMD(new Date(y, m - 1, d)) === value
}

const addMonthsYMD = (value: string, months: number) => {
  const [y, m, d] = value.split('-').map(Number)
  return toYMD(new Date(y, m - 1 + months, d))
}

const isGardeService = (service: ServiceSlug) => serviceConfig[service].formType === 'garde'

const parseAddress = (query: ParsedUrlQuery): SearchQuery['address'] => {
  const formatted = getSingleQueryParam(query.address)
  const latitude = parseNumberQueryParam(query.lat)
  const longitude = parseNumberQueryParam(query.lng)
  if (!formatted || latitude == null || longitude == null) return null
  return { formatted, latitude, longitude }
}

const parsePets = (query: ParsedUrlQuery): SearchQuery['pets'] => {
  if (!PET_SLUGS.some((slug) => getSingleQueryParam(query[slug]) != null)) return null
  return PET_SLUGS.map((slug) => ({
    slug,
    number: Math.max(0, Math.floor(parseNumberQueryParam(query[slug]) ?? 0)),
  }))
}

// Mêmes règles que DateTimeForm / DateTimeFormRecurrent : pas de date passée, fin >= début,
// et pour les promenades / visites pas plus de 3 mois. Une date invalide est ignorée (le formulaire la redemandera).
const parsePeriod = (query: ParsedUrlQuery, service: ServiceSlug | null, today: string): SearchQuery['period'] => {
  const start = getSingleQueryParam(query.start)
  const end = getSingleQueryParam(query.end)
  if (!isValidYMD(start) || start < today) return null

  const maxEnd = service && !isGardeService(service) ? addMonthsYMD(start, MAX_RECURRENT_MONTHS) : null
  const isValidEnd = isValidYMD(end) && end >= start && (!maxEnd || end <= maxEnd)
  return { start, end: isValidEnd ? end : null }
}

// Horaires par pas de 30 min ; un horaire déjà passé aujourd'hui est ignoré (désactivé dans l'app)
const parseTime = (value: string | undefined, isToday: boolean, now: Date) => {
  if (!value || !/^([01]\d|2[0-3]):(00|30)$/.test(value)) return ''
  return isToday && value <= toHM(now) ? '' : value
}

const parseSchedule = (
  query: ParsedUrlQuery,
  service: ServiceSlug | null,
  period: SearchQuery['period'],
  now: Date,
): SearchQuery['schedule'] => {
  if (!service || isGardeService(service)) return null

  const rawFrequency = getSingleQueryParam(query.frequency)
  const days = parseListQueryParam(query.days)
  const times = parseListQueryParam(query.times)
  const durations = parseListQueryParam(query.durations)
  if (rawFrequency == null && !days && !times && !durations) return null

  const frequency = (RECURRENT_FREQUENCIES as readonly string[]).includes(rawFrequency ?? '')
    ? (rawFrequency as Frequency)
    : null

  const customDays = days
    ? (Object.fromEntries(
        WEEKDAY_FIELDS.map((field, i) => [field, days.includes(WEEKDAY_SLUGS[i])]),
      ) as DateTimeInstance['customDays'])
    : null

  const isToday = period?.start === toYMD(now)
  const durationValues = durationOptions.map((option) => option.value)
  const count = Math.min(Math.max(times?.length ?? 0, durations?.length ?? 0), MAX_SERVICE_DAY_COUNT)
  const timePeriods = Array.from({ length: count }, (_, i) => {
    const duration = durations?.[i] ?? ''
    return {
      timeStart: parseTime(times?.[i], isToday, now),
      serviceDuration: durationValues.includes(duration) ? duration : '',
    }
  })

  return { frequency, customDays, timePeriods }
}

export function parseSearchQuery(query: ParsedUrlQuery, now = new Date()): SearchQuery {
  const service = getSlotType(getSingleQueryParam(query.service))
  const period = parsePeriod(query, service, toYMD(now))
  return {
    service,
    address: parseAddress(query),
    pets: parsePets(query),
    period,
    schedule: parseSchedule(query, service, period, now),
    source: getSingleQueryParam(query.src) || null,
  }
}

export const hasSearchQuery = (query: SearchQuery) =>
  !!(query.service || query.address || query.pets || query.period || query.schedule)

// Données du créneau pré-rempli dans le formulaire de l'app, ou null s'il n'y a rien à pré-remplir
const toSlotData = (service: ServiceSlug, { period, schedule }: SearchQuery): DateTimeInstance | null => {
  if (!period && !schedule) return null
  const base = { ...serviceConfig[service].initialValues, dateStart: period?.start ?? '' }

  if (isGardeService(service)) return { ...base, dateEnd: period?.end ?? '' }

  // Sans fréquence explicite : "un seul jour" si une seule date, sinon la fréquence est demandée dans le formulaire
  const frequency =
    schedule?.frequency ??
    (!period?.end || period.end === period.start ? 'once' : ('' as DateTimeInstance['frequency']))
  const timePeriods = schedule?.timePeriods.length ? schedule.timePeriods : base.timePeriods

  return {
    ...base,
    dateEnd: frequency === 'once' ? '' : period?.end ?? '',
    frequency,
    serviceDayCount: timePeriods.length,
    timePeriods,
    ...(frequency === 'custom' && schedule?.customDays && { customDays: schedule.customDays }),
  }
}

// Tout ce qu'il faut pour afficher directement les résultats sans repasser par le formulaire
export const isCompleteSearchQuery = (query: SearchQuery) => {
  const { service, period, schedule } = query
  if (!service || !query.address || !query.pets?.some((pet) => pet.number > 0) || !period) return false
  if (isGardeService(service)) return !!period.end

  // Promenade / visite : mêmes champs obligatoires que DateTimeFormRecurrent, 30 passages max
  if (!schedule?.frequency) return false
  if (schedule.frequency !== 'once' && !period.end) return false
  if (!schedule.timePeriods.length) return false
  if (schedule.timePeriods.some((tp) => !tp.timeStart || !tp.serviceDuration)) return false

  const dates = buildRecurrentDateList({
    dateStart: period.start,
    dateEnd: period.end ?? '',
    frequency: schedule.frequency,
    customDays: schedule.customDays ?? undefined,
  })
  const total = dates.length * schedule.timePeriods.length
  return total > 0 && total <= MAX_RECURRENT_SLOTS
}

export function searchQueryToStorePatch(
  query: SearchQuery,
  slots: { current: SlotsByService; blank: SlotsByService },
): SearchStorePatch {
  const isExternal = !!query.source
  const patch: SearchStorePatch = isExternal
    ? {
        selectedService: null,
        petsToComplete: PET_SLUGS.map((slug) => ({ slug, number: 0 })),
        formSlotsByService: slots.blank,
        hasCompleteSearch: isCompleteSearchQuery(query),
      }
    : {}

  if (query.service) {
    patch.selectedService = query.service
    patch.distance = getDefaultSearchDistance(query.service)
  }
  if (query.address) patch.address = query.address
  if (query.pets) patch.petsToComplete = query.pets

  const { service } = query
  const slotData = service ? toSlotData(service, query) : null
  if (service && slotData) {
    patch.formSlotsByService = {
      ...(patch.formSlotsByService ?? slots.current),
      [service]: [makeBlankSlot(service, slotData)],
    }
  }

  return patch
}
